// Follow Up touch/outcome constants (Follow Up P3 — spec §9.1–9.4 and the
// deployed follow_up_* RPC contract). Stored values are UPPER_SNAKE strings
// validated server-side; labels are display-only.
//
// isConnected is DERIVED SERVER-SIDE by
// public.follow_up_connection_result_is_connected(type, result) — the mirror
// below exists only for honest client display (a "Sent" text is never shown
// as a connection). Keep the two in sync.

export const FOLLOW_UP_TOUCH_TYPES = [
    { value: 'PHONE_CALL', label: 'Phone Call', icon: 'call-outline' },
    { value: 'IN_PERSON', label: 'In Person', icon: 'people-outline' },
    { value: 'TEXT', label: 'Text', icon: 'chatbubble-outline' },
    { value: 'EMAIL', label: 'Email', icon: 'mail-outline' },
    { value: 'OTHER', label: 'Other', icon: 'ellipsis-horizontal-outline' },
];

// Channel-appropriate connection results (spec §9.2). Only the connected
// results count toward "last connected" — everything else is an attempt.
export const FOLLOW_UP_CONNECTION_RESULTS = {
    PHONE_CALL: [
        { value: 'REACHED', label: 'Reached' },
        { value: 'NO_ANSWER', label: 'No Answer' },
        { value: 'VOICEMAIL', label: 'Voicemail' },
        { value: 'COULD_NOT_COMPLETE', label: "Couldn't Complete" },
    ],
    IN_PERSON: [
        { value: 'MET_VISITED', label: 'Met / Visited' },
        { value: 'ATTEMPTED_NOT_REACHED', label: 'Attempted — Not Reached' },
    ],
    TEXT: [
        { value: 'REPLIED', label: 'Replied / Conversation' },
        { value: 'SENT', label: 'Sent — No Reply' },
        { value: 'COULD_NOT_SEND', label: "Couldn't Send" },
    ],
    EMAIL: [
        { value: 'REPLIED', label: 'Replied / Conversation' },
        { value: 'SENT', label: 'Sent — No Reply' },
        { value: 'COULD_NOT_SEND', label: "Couldn't Send" },
    ],
    OTHER: [
        { value: 'CONNECTED', label: 'Connected' },
        { value: 'NOT_CONNECTED', label: 'Not Connected' },
    ],
};

// Optional response sentiment — separate from the connection result so a
// connected-but-negative conversation still counts as connected (§9.2/9.4).
export const FOLLOW_UP_DISPOSITIONS = [
    { value: 'RECEPTIVE', label: 'Receptive' },
    { value: 'NEUTRAL', label: 'Neutral' },
    { value: 'DECLINED', label: 'Declined' },
    { value: 'REQUESTED_NO_CONTACT', label: 'Requested No Contact' },
];

// Care-task purposes (follow_up_create_task / FollowUpTaskContexts.purpose).
// Purpose determines the completion contract: OUTREACH requires a structured
// touch, MEAL/APPROVAL record a simple outcome, OTHER completes with an
// optional touch (spec §10.3).
export const FOLLOW_UP_TASK_PURPOSES = [
    { value: 'OUTREACH', label: 'Outreach', icon: 'call-outline', outcome: null, description: 'Contact the person and record the result.' },
    { value: 'MEAL', label: 'Meal', icon: 'restaurant-outline', outcome: 'DELIVERED', description: 'Prepare or deliver a meal.' },
    { value: 'APPROVAL', label: 'Approval', icon: 'checkmark-done-outline', outcome: 'APPROVED', description: 'Review and approve.' },
    { value: 'OTHER', label: 'Other', icon: 'clipboard-outline', outcome: null, description: 'Other Follow Up work.' },
];

export const getTaskPurpose = (purpose) =>
    FOLLOW_UP_TASK_PURPOSES.find((p) => p.value === purpose) || FOLLOW_UP_TASK_PURPOSES[3];

export const getTaskPurposeLabel = (purpose) => getTaskPurpose(purpose).label;
export const getTaskPurposeIcon = (purpose) => getTaskPurpose(purpose).icon;

export const FOLLOW_UP_MONITORING_MODES = [
    { value: 'AUTO', label: 'Automatic', description: 'Included in monitoring based on the organization\u2019s audience rules.' },
    { value: 'INCLUDE', label: 'Always Include', description: 'Always monitored, regardless of audience rules.' },
    { value: 'EXCLUDE', label: 'Excluded', description: 'Not monitored by Follow Up.' },
];

// PeopleNotes.visibility values (spec §8.3). Server keeps the legacy
// isPrivate column synced; clients write both for old-server compat.
export const NOTE_VISIBILITIES = {
    PUBLIC: 'PUBLIC',
    PRIVATE: 'PRIVATE',
    FOLLOW_UP: 'FOLLOW_UP',
};

export const NOTE_VISIBILITY_OPTIONS = [
    { value: 'PUBLIC', label: 'Visible to organization', subtitle: 'Anyone who can view people notes' },
    { value: 'PRIVATE', label: 'Private — only me & admins', subtitle: 'Hidden from everyone except you and admins' },
    { value: 'FOLLOW_UP', label: 'Follow Up team', subtitle: 'Visible to the Follow Up care team' },
];

// Client mirror of follow_up_connection_result_is_connected — display only.
export const followUpResultIsConnected = (type, result) => {
    const t = `${type || ''}`.trim().toUpperCase();
    const r = `${result || ''}`.trim().toUpperCase();
    switch (t) {
        case 'PHONE_CALL': return r === 'REACHED';
        case 'IN_PERSON': return ['MET', 'VISITED', 'MET_VISITED'].includes(r);
        case 'TEXT':
        case 'EMAIL': return ['REPLIED', 'CONVERSATION'].includes(r);
        case 'OTHER': return r === 'CONNECTED';
        default: return false;
    }
};

const humanize = (value) => `${value || ''}`
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const getTouchTypeLabel = (type) =>
    FOLLOW_UP_TOUCH_TYPES.find((t) => t.value === type)?.label || humanize(type) || 'Touch';

export const getTouchTypeIcon = (type) =>
    FOLLOW_UP_TOUCH_TYPES.find((t) => t.value === type)?.icon || 'chatbubble-ellipses-outline';

export const getConnectionResultLabel = (type, result) => {
    const match = FOLLOW_UP_CONNECTION_RESULTS[type]?.find((r) => r.value === result);
    return match?.label || humanize(result) || 'Logged';
};

export const getDispositionLabel = (disposition) =>
    FOLLOW_UP_DISPOSITIONS.find((d) => d.value === disposition)?.label || humanize(disposition);

export const getMonitoringModeLabel = (mode) =>
    FOLLOW_UP_MONITORING_MODES.find((m) => m.value === mode)?.label || 'Automatic';

// Attention reasons (P5 — spec §11.5, follow_up_get_overview /
// follow_up_list_people / follow_up_get_my_follow_up / follow_up_get_person).
// NO_ATTENDANCE_RECORD wording is contract-locked: "No recorded qualifying
// attendance" — never imply proven absence (spec §11.3 QA gate).
export const FOLLOW_UP_ATTENTION_REASONS = [
    {
        value: 'ATTEMPT_GAP',
        label: 'Outreach overdue',
        description: "It's been too long since anyone tried to reach out.",
        icon: 'call-outline',
        snoozable: true,
    },
    {
        value: 'CONNECTED_GAP',
        label: 'No recent connection',
        description: "Attempts are happening but no real conversation has connected lately.",
        icon: 'chatbubbles-outline',
        snoozable: true,
    },
    {
        value: 'ATTENDANCE_GAP',
        label: 'Attendance gap',
        description: "They haven't been recorded at a qualifying event within the attendance window.",
        icon: 'calendar-outline',
        snoozable: true,
    },
    {
        value: 'NO_ATTENDANCE_RECORD',
        label: 'No recorded qualifying attendance',
        description: "No qualifying attendance is on record — this does not prove absence.",
        icon: 'calendar-clear-outline',
        snoozable: true,
    },
    {
        value: 'NEEDS_OWNER',
        label: 'Needs a primary connector',
        description: 'Open Follow Up work has no primary connector.',
        icon: 'person-add-outline',
        snoozable: false,
    },
];

export const getAttentionReason = (reason) =>
    FOLLOW_UP_ATTENTION_REASONS.find((r) => r.value === reason)
    || { value: reason, label: humanize(reason) || 'Attention', description: '', icon: 'alert-circle-outline', snoozable: true };

export const getAttentionReasonLabel = (reason) => getAttentionReason(reason).label;

export const getAttentionReasonIcon = (reason) => getAttentionReason(reason).icon;

/**
 * Normalizes the `snoozed` field on an attention-reason object. The RPC may
 * send false/null, a bare `until` timestamp string, or an object
 * ({until, reason}). Returns {until, reason} or null when not snoozed.
 */
export const getAttentionReasonSnooze = (reason) => {
    const s = reason?.snoozed;
    if (!s) return null;
    if (typeof s === 'string') return { until: s, reason: null };
    if (typeof s === 'object') {
        return {
            until: s.until ?? s.snoozedUntil ?? reason?.snoozedUntil ?? null,
            reason: s.reason ?? s.snoozeReason ?? null,
        };
    }
    return { until: reason?.snoozedUntil ?? null, reason: null };
};

/** Short absolute date for "Snoozed until <date>" labels (relative phrases read wrong there). */
export const formatSnoozeUntil = (until) => {
    if (!until) return null;
    const d = new Date(until);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ---------------------------------------------------------------------------
// Flat Follow Up Paths (§25). One ordered step list — no stages,
// requirements, evidence timing, kinds, or pause/resume. Values mirror
// FollowUpSteps / FollowUpStepProgress / follow_up_publish_path in
// reachchms-functions/supabase/schemas — labels are display-only.
// ---------------------------------------------------------------------------

// The single grouped action picker (§25.5 step action catalog). Each option
// maps to a stored actionType (+ channel for OUTREACH). `human` actions
// create a Task at activation and carry assignment/deadline fields;
// automatic sends never do.
export const FOLLOW_UP_STEP_ACTION_GROUPS = [
    {
        label: 'Automatic messages',
        options: [
            { actionID: 'SEND_EMAIL', actionType: 'SEND_EMAIL', label: 'Send automatic email', icon: 'mail-outline', human: false, description: 'Sends an existing email template to the person.' },
            { actionID: 'SEND_SMS', actionType: 'SEND_SMS', label: 'Send automatic text', icon: 'chatbubble-outline', human: false, description: 'Sends the written text to the person.' },
        ],
    },
    {
        label: 'Personal follow-up',
        options: [
            { actionID: 'OUTREACH:PHONE_CALL', actionType: 'OUTREACH', channel: 'PHONE_CALL', label: 'Phone call', icon: 'call-outline', human: true },
            { actionID: 'OUTREACH:IN_PERSON', actionType: 'OUTREACH', channel: 'IN_PERSON', label: 'In-person visit', icon: 'people-outline', human: true },
            { actionID: 'OUTREACH:TEXT', actionType: 'OUTREACH', channel: 'TEXT', label: 'Personal text', icon: 'chatbubble-outline', human: true },
            { actionID: 'OUTREACH:EMAIL', actionType: 'OUTREACH', channel: 'EMAIL', label: 'Personal email', icon: 'mail-outline', human: true },
            { actionID: 'OUTREACH:OTHER', actionType: 'OUTREACH', channel: 'OTHER', label: 'Other touch', icon: 'ellipsis-horizontal-outline', human: true },
        ],
    },
    {
        label: 'Other work',
        options: [
            { actionID: 'TASK', actionType: 'TASK', label: 'Custom task', icon: 'clipboard-outline', human: true, description: 'Any other follow-up work a person completes.' },
        ],
    },
];

export const FOLLOW_UP_STEP_ACTIONS = FOLLOW_UP_STEP_ACTION_GROUPS.flatMap((g) => g.options);

export const getStepAction = (actionID) =>
    FOLLOW_UP_STEP_ACTIONS.find((a) => a.actionID === actionID) || null;

/** Map a stored/draft step back to its picker action (actionType + channel). */
export const getStepActionID = (step) => {
    if (!step?.actionType) return null;
    if (step.actionType === 'OUTREACH') {
        return `OUTREACH:${step?.config?.channel || step?.channel || 'PHONE_CALL'}`;
    }
    return step.actionType;
};

export const getStepActionForStep = (step) => getStepAction(getStepActionID(step));

/** Human work (generates a Task) vs automatic sends (no Task, no assignment). */
export const stepActionIsHuman = (actionType) => actionType === 'OUTREACH' || actionType === 'TASK';

// Truthful action labels for definition steps and progress rows (§25.5 —
// automatic and personal messages must read distinctly).
export const getStepActionLabel = (step) => {
    if (!step?.actionType) return 'Step';
    if (step.actionType === 'SEND_EMAIL') return 'Send automatic email';
    if (step.actionType === 'SEND_SMS') return 'Send automatic text';
    if (step.actionType === 'OUTREACH') {
        return getTouchTypeLabel(step?.config?.channel || step?.channel) || 'Personal touch';
    }
    if (step.actionType === 'TASK') return 'Custom task';
    return humanize(step.actionType) || 'Step';
};

export const getStepActionIcon = (step) => {
    const action = getStepActionForStep(step);
    if (action?.icon) return action.icon;
    if (step?.actionType === 'OUTREACH') return getTouchTypeIcon(step?.config?.channel);
    return 'ellipse-outline';
};

// config.assignment.mode for human steps (§25.7 — exactly three choices;
// there is no Unassigned authoring mode).
export const FOLLOW_UP_STEP_ASSIGNMENT_MODES = [
    { value: 'SPECIFIC', label: 'Specific person', description: 'Always assigned to the chosen person.' },
    { value: 'PRIMARY_CONNECTOR', label: 'Primary connector', description: "Assigned to the person's primary connector when the step starts." },
    { value: 'POOL', label: 'Assignment pool', description: 'Offered to the selected people in turn.' },
];

export const getStepAssignmentModeLabel = (mode) =>
    FOLLOW_UP_STEP_ASSIGNMENT_MODES.find((m) => m.value === mode)?.label || humanize(mode);

// FollowUpStepProgress.status CHECK values (§25.6). Only SUCCEEDED /
// EXPIRED / SKIPPED advance the sequence; CANCELLED comes from a stopped
// enrollment and RETIRED from a template edit of unstarted work.
export const FOLLOW_UP_STEP_STATUSES = {
    PENDING: { label: 'Upcoming', colorVariant: 'textSecondary', icon: 'ellipse-outline' },
    WAITING: { label: 'Scheduled', colorVariant: 'textSecondary', icon: 'time-outline' },
    ACTIVE: { label: 'Current', colorVariant: 'paths', icon: 'play-circle-outline' },
    NEEDS_ATTENTION: { label: 'Needs attention', colorVariant: 'warning', icon: 'alert-circle-outline' },
    SUCCEEDED: { label: 'Done', colorVariant: 'success', icon: 'checkmark-circle-outline' },
    EXPIRED: { label: 'Expired', colorVariant: 'warning', icon: 'timer-outline' },
    SKIPPED: { label: 'Skipped', colorVariant: 'textSecondary', icon: 'play-skip-forward-outline' },
    CANCELLED: { label: 'Cancelled', colorVariant: 'textSecondary', icon: 'close-circle-outline' },
    RETIRED: { label: 'Removed', colorVariant: 'textTertiary', icon: 'eye-off-outline' },
};

export const getStepStatusMeta = (status) =>
    FOLLOW_UP_STEP_STATUSES[status]
    || { label: humanize(status) || 'Status', colorVariant: 'textSecondary', icon: 'help-circle-outline' };

// Structured stop metadata on the enrollment (§25.6/§25.7). Anything
// unknown humanizes so a future reason never renders as a raw code.
export const FOLLOW_UP_STOP_REASONS = {
    MANUAL: 'Stopped by team',
    REQUESTED_NO_CONTACT: 'Requested no contact',
};

export const getStopReasonLabel = (reason) =>
    FOLLOW_UP_STOP_REASONS[reason] || humanize(reason) || 'Stopped';

// FollowUpEnrollments.status values (§25.6): stored codes kept, display
// names are Active / Finished / Stopped. CLOSED remains for legacy care
// history only — the flat model never produces it.
export const FOLLOW_UP_ENROLLMENT_STATUSES = {
    ACTIVE: { label: 'Active', colorVariant: 'paths', icon: 'play-circle-outline' },
    COMPLETED: { label: 'Finished', colorVariant: 'success', icon: 'checkmark-circle-outline' },
    CANCELLED: { label: 'Stopped', colorVariant: 'error', icon: 'close-circle-outline' },
    CLOSED: { label: 'Closed', colorVariant: 'textSecondary', icon: 'archive-outline' },
};

export const getEnrollmentStatusMeta = (status) =>
    FOLLOW_UP_ENROLLMENT_STATUSES[status]
    || { label: humanize(status) || 'Status', colorVariant: 'textSecondary', icon: 'help-circle-outline' };

/**
 * Meal-slot display state (spec §13.4/C10). The slot row's `status` is only
 * OPEN/CANCELLED — that is the need. Everything else derives:
 *   DELIVERED  task completed (deliveredAt stamped server-side)
 *   CLAIMED    staffed (an assignee exists) but not delivered — a
 *              commitment, not a delivery; never render as delivered
 *   OPEN       unstaffed and claimable
 *   CANCELLED  explicit removal of the needed date (history, never deleted)
 */
export const FOLLOW_UP_MEAL_SLOT_STATES = {
    DELIVERED: { label: 'Delivered', colorVariant: 'success', icon: 'checkmark-circle-outline' },
    CLAIMED: { label: 'Claimed, not delivered', colorVariant: 'paths', icon: 'hand-left-outline' },
    OPEN: { label: 'Needs a volunteer', colorVariant: 'warning', icon: 'restaurant-outline' },
    CANCELLED: { label: 'Cancelled', colorVariant: 'textSecondary', icon: 'close-circle-outline' },
};

// The documented careDetails keys (FollowUpEnrollments.careDetails, spec
// §7.5). Free-text coordination details — all optional, none block intake.
export const FOLLOW_UP_CARE_DETAIL_KEYS = [
    'location',
    'locationInstructions',
    'visitPreferences',
    'coordinationContact',
    'reviewDate',
    'deliveryInstructions',
];
